// EDIFACT-Grundfunktionen für Konverter, Viewer, Formatter und Validator.
// Berücksichtigt die Trennzeichen aus UNA, das Freigabezeichen (?) und den Zeichensatz der Datei.
// Reine Funktionen ohne React – alles läuft lokal im Browser.

const DEFAULT_SEPARATORS = { component: ":", element: "+", decimal: ".", release: "?", segment: "'" };

/**
 * Datei-Inhalt in Text umwandeln: gültiges UTF-8 wird als UTF-8 gelesen,
 * sonst Windows-1252/Latin-1 (typisch für UNOC-Dateien mit Umlauten).
 */
export function decodeEdifact(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let text;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    text = new TextDecoder("windows-1252").decode(bytes);
  }
  return text.replace(/^﻿/, "");
}

/** Trennzeichen aus dem UNA-Segment lesen, sonst die Standardwerte. */
export function getSeparators(text) {
  const t = text.trimStart();
  if (t.startsWith("UNA") && t.length >= 9) {
    return {
      component: t[3],
      element: t[4],
      decimal: t[5],
      release: t[6] === " " ? null : t[6], // Leerzeichen = kein Freigabezeichen
      segment: t[8],
      una: t.slice(0, 9),
    };
  }
  return { ...DEFAULT_SEPARATORS, una: null };
}

/** Grobe Prüfung, ob der Text wie EDIFACT aussieht (Segmentname + Trennzeichen). */
export function looksLikeEdifact(text) {
  const t = (text || "").trimStart();
  const sep = getSeparators(t);
  const body = sep.una ? t.slice(9).trimStart() : t;
  if (!body.includes(sep.element) || !body.includes(sep.segment)) return false;
  return /^[A-Z][A-Z0-9]{2}$/.test(body.slice(0, 3)) && body[3] === sep.element;
}

// Zerlegt an einem Trennzeichen, das nicht durch das Freigabezeichen geschützt ist.
function splitOn(s, ch, release) {
  const parts = [];
  let start = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === release) { i++; continue; }
    if (s[i] === ch) { parts.push(s.slice(start, i)); start = i + 1; }
  }
  parts.push(s.slice(start));
  return parts;
}

/** Text in Segmente zerlegen (ohne Segmentende-Zeichen). UNA bleibt als eigenes Segment erhalten. */
export function splitSegments(text, sep = getSeparators(text)) {
  let t = text.trimStart();
  const segments = [];
  if (sep.una) {
    segments.push(sep.una);
    t = t.slice(9);
  }
  for (const raw of splitOn(t, sep.segment, sep.release)) {
    const s = raw.replace(/^\s+/, "").replace(/[\r\n]+$/, "");
    if (s) segments.push(s);
  }
  return segments;
}

/** Segment in Datenelemente zerlegen. Werte bleiben roh (Freigabezeichen bleiben erhalten). */
export function splitElements(segment, sep) {
  if (sep.una && segment === sep.una) return ["UNA", segment.slice(3)];
  return splitOn(segment, sep.element, sep.release);
}

/** Datenelement in Komponenten zerlegen (roh). */
export function splitComponents(element, sep) {
  return splitOn(element, sep.component, sep.release);
}

/** Freigabezeichen entfernen: "O?'Brien" -> "O'Brien". */
export function unescapeValue(value, sep) {
  if (!sep.release || !value.includes(sep.release)) return value;
  let out = "";
  for (let i = 0; i < value.length; i++) {
    if (value[i] === sep.release && i + 1 < value.length) { out += value[i + 1]; i++; }
    else out += value[i];
  }
  return out;
}

/** Lesbarer Wert eines Datenelements: Komponenten mit ":" verbunden, Freigabezeichen entfernt. */
export function displayElement(element, sep) {
  return splitComponents(element, sep).map(c => unescapeValue(c, sep)).join(":");
}

/**
 * Tabelle für Anzeige und Export: Kopfzeile ("Segment", "Element 1", …) + eine Zeile pro Segment.
 * raw = true lässt die Werte unverändert (für den Formatter, der die Datei wieder schreibt).
 */
export function toTable(text, { raw = false } = {}) {
  const sep = getSeparators(text);
  const rows = splitSegments(text, sep).map(seg => {
    const parts = splitElements(seg, sep);
    return raw || parts[0] === "UNA" ? parts : parts.map(p => displayElement(p, sep));
  });
  let max = 1;
  for (const r of rows) if (r.length > max) max = r.length;
  const head = ["Segment", ...Array.from({ length: max - 1 }, (_, i) => `Element ${i + 1}`)];
  return { sep, rows: [head, ...rows.map(r => (r.length < max ? [...r, ...Array(max - r.length).fill("")] : r))] };
}

// Element- und Segment-Trennzeichen in einem Wert freigeben, vorhandene Freigaben übernehmen.
function releaseSeparators(value, sep) {
  if (!sep.release) return value;
  let out = "";
  for (let i = 0; i < value.length; i++) {
    const c = value[i];
    if (c === sep.release) {
      if (i + 1 < value.length) { out += c + value[i + 1]; i++; }
      else out += c + c; // einzelnes Freigabezeichen am Ende würde das folgende Trennzeichen schützen
    } else if (c === sep.element || c === sep.segment) {
      out += sep.release + c;
    } else {
      out += c;
    }
  }
  return out;
}

/**
 * Rohe Tabellenzeilen (ohne Kopfzeile) wieder als EDIFACT schreiben – ein Segment pro Zeile.
 * Leere Elemente am Segmentende und komplett leere Zeilen werden weggelassen.
 */
export function writeSegments(rows, sep) {
  const lines = [];
  for (const row of rows) {
    if (row[0] === "UNA") {
      if (sep.una) lines.push(sep.una);
      continue;
    }
    const els = row.map(v => releaseSeparators(String(v ?? ""), sep));
    while (els.length > 1 && els[els.length - 1] === "") els.pop();
    if (els.every(v => v === "")) continue;
    lines.push(els.join(sep.element) + sep.segment);
  }
  return lines.join("\n");
}

/** Datei lesen und prüfen. Liefert { text } oder { error } (Meldung für die Oberfläche). */
export function readEdifactFile(file, maxBytes) {
  return new Promise(resolve => {
    if (file.size > maxBytes) {
      resolve({ error: `❌ Datei zu groß (max. ${Math.round(maxBytes / 1024 / 1024)} MB erlaubt).` });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = decodeEdifact(reader.result);
      resolve(looksLikeEdifact(text) ? { text } : { error: "❌ Keine gültige EDIFACT-Datei." });
    };
    reader.onerror = () => resolve({ error: "❌ Fehler beim Lesen der Datei." });
    reader.readAsArrayBuffer(file);
  });
}
